import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import PageHeader from '@/components/PageHeader';
import AdvanceShareStyleForm from './advance-share-style-form';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { AdvanceShareStyle } from '@/models/advance-share-style.model';
import { getAdvanceShareStyle } from '@/services/advance-share-style.service';
import AdvanceShareStyleVersionList from '../version/list/advance-share-style-version-list';
import AdvanceShareStyleActions from './advance-share-style-actions';
import AdvanceShareList from '../../list/advance-share-list';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="style-form">Style Detail</Nav.Item>
      <Nav.Item eventKey="style-versions" disabled={newMode}>Versions</Nav.Item>
      <Nav.Item eventKey="style-actions" disabled={newMode}>Actions</Nav.Item>
      <Nav.Item eventKey="style-generate-requests" disabled={newMode}>Generate Requests</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('style-form');
  const [formObject, setFormObject] = useState<AdvanceShareStyle|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<AdvanceShareStyle> = await getAdvanceShareStyle(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Style: ${errorModel?.errorMessage}`);
        nav('/advance-shares/styles');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Style: ${formObject.name}` : 'New Style'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item >Advance Shares</Breadcrumb.Item>
            <Breadcrumb.Item href="/advance-shares/styles">Styles</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'style-form' && <AdvanceShareStyleForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'style-versions' && id && <AdvanceShareStyleVersionList style={formObject}/>}
          {activeTab == 'style-actions' && <AdvanceShareStyleActions formObject={formObject} fetchData={fetchData} />}
          {activeTab == 'style-generate-requests' && <AdvanceShareList styleId={formObject?.id} />}
        </>
      }
    </Panel>
  );
};

export default Page;
